import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { fulfillmentApi } from '../api/fulfillment-api';
import { shipmentsApi } from '../api/shipments-api';
import { ErrorState } from '../components/ErrorState';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import { SearchableSelect } from '../components/SearchableSelect';
import type { FulfillmentOrderLine } from '../domain/fulfillment';
import { useFulfillmentReferences } from '../features/fulfillment/use-fulfillment-references';
import {
  filterShippedLines,
  shippedLines,
  shippedLinesCsv,
  summarizeShippedItems,
} from '../features/shipments/shipped-items';
import { humanizeIdentifier } from '../lib/formatting';
import { siteSearchAliases } from '../lib/site-label';

const DETAIL_LIMIT = 200;

/**
 * What was shipped, from handed-over pick lists (tidaltech/koi#8206): e.g.
 * how many umbilicals left last quarter, or how many surface cameras went
 * to one site.
 */
export function ShippedItemsPage() {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(
    () => ({
      query: params.get('q') ?? '',
      from: params.get('from') ?? '',
      to: params.get('to') ?? '',
      customerId: params.get('customer') ?? '',
      siteId: params.get('site') ?? '',
    }),
    [params],
  );

  function setParam(name: string, value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set(name, value);
    else next.delete(name);
    setParams(next, { replace: true });
  }

  const references = useFulfillmentReferences();
  const orders = useQuery({
    queryKey: ['fulfillment-orders', { states: ['COMPLETED'] }],
    queryFn: () => fulfillmentApi.listOrders({ states: ['COMPLETED'] }),
  });
  const shipments = useQuery({
    queryKey: ['shipments'],
    queryFn: () => shipmentsApi.listShipments(),
  });

  const lines = useMemo(() => {
    const partType = new Map(references.parts.map((part) => [part.id, part.type]));
    const serializedName = (line: FulfillmentOrderLine) => {
      if (line.requirement.label) return line.requirement.label;
      const type = partType.get(line.requirement.partIds[0] ?? '');
      return type ? humanizeIdentifier(type) : 'Serialized item';
    };
    return shippedLines(orders.data ?? [], {
      serialized: serializedName,
      stock: (id) => references.stockItem(id)?.displayName ?? id,
      unit: references.unit,
    });
  }, [orders.data, references]);

  const filtered = useMemo(() => filterShippedLines(lines, filters), [lines, filters]);
  const summary = useMemo(() => summarizeShippedItems(filtered), [filtered]);

  // Shipments in the period with no handed-over pick list aren't counted.
  const uncounted = useMemo(() => {
    const covered = new Set((orders.data ?? []).map((order) => order.shipmentId).filter(Boolean));
    return (shipments.data?.shipments ?? []).filter((shipment) => {
      const day = shipment.shippedAt.slice(0, 10);
      if (filters.from && day < filters.from) return false;
      if (filters.to && day > filters.to) return false;
      if (filters.siteId && shipment.destinationSiteId !== filters.siteId) return false;
      if (filters.customerId && shipment.destinationCustomerId !== filters.customerId) {
        return false;
      }
      return shipment.direction === 'OUTBOUND' && !covered.has(shipment.id);
    }).length;
  }, [orders.data, shipments.data, filters]);

  const customerOptions = useMemo(
    () => [
      { value: '', label: 'All customers' },
      ...references.customers.map((customer) => ({
        value: customer.id,
        label: references.customerName(customer.id),
      })),
    ],
    [references],
  );
  const siteOptions = useMemo(
    () => [
      { value: '', label: 'All sites' },
      ...references.sites
        .filter((site) => !filters.customerId || site.customerId === filters.customerId)
        .map((site) => ({
          value: site.id,
          label: references.siteName(site.id),
          aliases: siteSearchAliases(site),
        })),
    ],
    [references, filters.customerId],
  );

  function downloadCsv(): void {
    const csv = shippedLinesCsv(filtered, {
      customer: references.customerName,
      site: references.siteName,
    });
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `shipped-items-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  if (orders.isLoading) return <PageLoadingState label="Loading shipped items…" />;
  if (orders.isError) {
    return (
      <ErrorState
        title="Shipped items could not be loaded"
        rpcName="ListFulfillmentOrders"
        onRetry={() => void orders.refetch()}
      />
    );
  }

  return (
    <>
      <PageHeader
        backLink={<Link to="/shipments">Shipments</Link>}
        title="Shipped items"
        description="What left on handed-over pick lists, by item, period, customer and site."
        actions={
          <button type="button" onClick={downloadCsv} disabled={filtered.length === 0}>
            Download CSV
          </button>
        }
      />

      <form
        className="requests-controls"
        role="search"
        aria-label="Filter shipped items"
        onSubmit={(event) => event.preventDefault()}
      >
        <label className="search-field">
          <span>Item or serial</span>
          <input
            type="search"
            value={filters.query}
            onChange={(event) => setParam('q', event.target.value)}
            placeholder="e.g. Surface camera, umbilical, VVCS…"
            aria-label="Search shipped items"
          />
        </label>
        <label>
          <span>From</span>
          <input
            type="date"
            value={filters.from}
            onChange={(event) => setParam('from', event.target.value)}
            aria-label="Shipped from"
          />
        </label>
        <label>
          <span>To</span>
          <input
            type="date"
            value={filters.to}
            onChange={(event) => setParam('to', event.target.value)}
            aria-label="Shipped to"
          />
        </label>
        <label>
          <span>Customer</span>
          <SearchableSelect
            ariaLabel="Customer"
            value={filters.customerId}
            onChange={(value) => {
              const next = new URLSearchParams(params);
              if (value) next.set('customer', value);
              else next.delete('customer');
              next.delete('site');
              setParams(next, { replace: true });
            }}
            options={customerOptions}
            placeholder="All customers"
          />
        </label>
        <label>
          <span>Site</span>
          <SearchableSelect
            ariaLabel="Site"
            value={filters.siteId}
            onChange={(value) => setParam('site', value)}
            options={siteOptions}
            placeholder="All sites"
          />
        </label>
      </form>

      {uncounted > 0 ? (
        <p className="field-hint" role="status">
          {uncounted} outbound {uncounted === 1 ? 'shipment' : 'shipments'} in this selection had no
          handed-over pick list, so {uncounted === 1 ? 'its' : 'their'} contents aren’t counted.
        </p>
      ) : null}

      <section aria-label="Totals by item">
        <h2>Totals by item</h2>
        {summary.length === 0 ? (
          <p className="empty-note">Nothing shipped matches these filters.</p>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Item</th>
                  <th scope="col">Quantity</th>
                  <th scope="col">Orders</th>
                  <th scope="col">Sites</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((row) => (
                  <tr key={`${row.item}/${row.unit}`}>
                    <th scope="row">
                      <button
                        type="button"
                        className="table-filter-link"
                        onClick={() => setParam('q', row.item)}
                      >
                        {row.item}
                      </button>
                    </th>
                    <td className="mono">
                      {row.quantity.toLocaleString()}
                      {row.unit ? ` ${row.unit}` : ''}
                    </td>
                    <td className="mono">{row.orders}</td>
                    <td className="mono">{row.sites}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {filtered.length > 0 ? (
        <section aria-label="Shipped lines">
          <h2>Shipped lines</h2>
          {filtered.length > DETAIL_LIMIT ? (
            <p className="field-hint">
              Showing the latest {DETAIL_LIMIT} of {filtered.length}. Download the CSV for all.
            </p>
          ) : null}
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Shipped</th>
                  <th scope="col">Item</th>
                  <th scope="col">Quantity</th>
                  <th scope="col">Serial numbers</th>
                  <th scope="col">Site</th>
                  <th scope="col">Order</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, DETAIL_LIMIT).map((line) => (
                  <tr key={line.key}>
                    <td className="mono">
                      {line.shippedAt ? new Date(line.shippedAt).toLocaleDateString() : '—'}
                    </td>
                    <td>{line.item}</td>
                    <td className="mono">
                      {line.quantity}
                      {line.unit ? ` ${line.unit}` : ''}
                    </td>
                    <td className="mono">{line.serials.join(', ') || '—'}</td>
                    <td>
                      {references.siteName(line.siteId)}
                      <p className="table-subtext">{references.customerName(line.customerId)}</p>
                    </td>
                    <td>
                      <Link to={`/fulfillment/${encodeURIComponent(line.orderId)}`}>
                        {line.orderTitle}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </>
  );
}
